import type {HMDocumentInfo, UnpackedHypermediaId} from '@seed-hypermedia/client/hm-types'
import {useInfiniteQuery, useQuery} from '@tanstack/react-query'
import {useMemo} from 'react'
import {
  BuiltinSortAttribute,
  ContentTypeFilter,
  DocumentAttributeKind,
  DocumentFilter,
  DocumentFilter_And,
  DocumentFilter_Or,
  DocumentFilter_PathMatch,
  DocumentFilter_SpaceMatch,
  DocumentFilter_URLMatch,
  DocumentSort,
  EntityKindFilter,
  ListAccountsRequest,
  ListDocumentAttributeNamesRequest,
  ListDocumentAttributeValuesRequest,
  QueryDocumentsRequest,
} from '../client/grpc-types'
import {
  compileExploreQuery,
  documentInfoToExploreResultDocument,
  searchResultItemToExploreResult,
  withExploreTypeFilter,
  type ExploreQueryNode,
  type HMExploreContext,
  type HMExploreMatchedField,
  type HMExploreResult,
  type HMExploreResultType,
  type ParsedExploreQuery,
} from '../explore'
import {useUniversalClient} from '../routing'
import {packHmId} from '../utils/entity-id-url'
import {useSelectedAccountContacts} from './contacts'
import {prepareHMDocumentInfo, useSelectedAccountId, useSiteMembers} from './entity'
import {queryKeys} from './query-keys'
import type {SearchResultItem} from './search'

/** A page returned by the document stream. */
export type ExploreDocumentPage = {
  documents: HMDocumentInfo[]
  nextPageToken: string
  truncatedByCap?: boolean
}

/** A page returned by the text stream. */
export type ExploreTextPage = {
  entities: SearchResultItem[]
  nextPageToken: string
}

/** Results assembled from the document and text streams. */
export type ExploreAssembly = {
  results: HMExploreResult[]
  documents: HMExploreResult[]
  blocks: HMExploreResult[]
  comments: HMExploreResult[]
  spaces: HMExploreResult[]
  contacts: HMExploreResult[]
  counts: Record<HMExploreResultType | 'all', number>
  textTerms: string[]
  diagnostics: ParsedExploreQuery['diagnostics']
  intersectionTruncated: boolean
  intersectionPending: boolean
  blocksByDocument: Record<string, Extract<HMExploreResult, {type: 'block'}>[]>
}

export const EXPLORE_INTERSECTION_DOCUMENT_CAP = 1000

/** Loads spaces and attribute suggestions shared by the Explore builder. */
export function useExploreAccounts(enabled = true) {
  const client = useUniversalClient()
  return useQuery({
    queryKey: [queryKeys.ENTITY, 'explore-accounts'],
    enabled: enabled && Boolean(client.listAccounts),
    queryFn: async () => {
      if (!client.listAccounts) return []
      const response = await client.listAccounts(new ListAccountsRequest({pageSize: 1000}))
      return response.accounts.map((account) => {
        const homeMetadata = account.homeDocumentInfo?.metadata?.toJson() as {name?: string} | undefined
        return {
          value: account.id,
          label: homeMetadata?.name || account.profile?.name || account.id,
        }
      })
    },
  })
}

/** Loads global document attribute names for autocomplete. */
export function useExploreAttributeNames(account = '', enabled = true) {
  const client = useUniversalClient()
  return useQuery({
    queryKey: [queryKeys.ENTITY, 'explore-attribute-names', account],
    enabled: enabled && Boolean(client.listDocumentAttributeNames),
    queryFn: async () => {
      if (!client.listDocumentAttributeNames) return []
      const names: string[] = []
      let pageToken = ''
      do {
        const response = await client.listDocumentAttributeNames(
          new ListDocumentAttributeNamesRequest({account, recursive: true, pageSize: 100, pageToken}),
        )
        names.push(...response.names.map((name) => name.name))
        pageToken = response.nextPageToken
      } while (pageToken)
      return names
    },
  })
}

/** Loads known values for one attribute and scalar kind. */
export function useExploreAttributeValues(path: string, kind: 'string' | 'int' | 'bool', prefix = '', enabled = true) {
  const client = useUniversalClient()
  const attributeKind =
    kind === 'string'
      ? DocumentAttributeKind.STRING
      : kind === 'int'
        ? DocumentAttributeKind.INT
        : DocumentAttributeKind.BOOL
  return useQuery({
    queryKey: [queryKeys.ENTITY, 'explore-attribute-values', path, kind, prefix],
    enabled: enabled && Boolean(path) && Boolean(client.listDocumentAttributeValues) && kind !== 'bool',
    queryFn: async () => {
      if (!client.listDocumentAttributeValues || kind === 'bool') return []
      const response = await client.listDocumentAttributeValues(
        new ListDocumentAttributeValuesRequest({path: path.split('.'), kind: attributeKind, prefix, pageSize: 30}),
      )
      return response.values.flatMap((item) => {
        const value = item.value?.value
        if (!value) return []
        if (value.case === 'stringValue' || value.case === 'intValue' || value.case === 'boolValue')
          return [String(value.value)]
        return []
      })
    },
  })
}

/** Describes which daemon streams are needed for a parsed Explore query. */
export function exploreStreamSelection(parsed: ParsedExploreQuery, context: HMExploreContext) {
  const compilation = compileExploreQuery(parsed, context)
  return {
    text: compilation.textTerms.length > 0,
    documents: compilation.documentPredicates.length > 0 && !!compilation.filter,
    intersection: compilation.textTerms.length > 0 && compilation.documentPredicates.length > 0 && !!compilation.filter,
  }
}

/** Returns a version-independent identity for a document or its descendants. */
export function exploreDocumentKey(id: {uid: string; path?: string[] | null}) {
  return `${id.uid}:${(id.path ?? []).join('/')}`
}

export function resultKey(result: HMExploreResult) {
  if (result.type === 'comment') return `comment:${exploreDocumentKey(result.documentId)}:${result.commentId}`
  if (result.type === 'block') {
    return `block:${exploreDocumentKey(result.id)}:${result.id.blockRef ?? ''}:${result.id.blockRange ?? ''}`
  }
  if (result.type === 'space') return `space:${exploreDocumentKey(result.id)}`
  if (result.type === 'contact') return `contact:${result.id.uid}`
  return `document:${exploreDocumentKey(result.id)}`
}

function parentKey(result: HMExploreResult) {
  if (result.type === 'comment') return exploreDocumentKey(result.documentId)
  if (result.type === 'block') {
    return exploreDocumentKey(result.id)
  }
  return exploreDocumentKey(result.id)
}

function referencedAttributes(parsed: ParsedExploreQuery) {
  const keys: string[] = []
  const visit = (node: ExploreQueryNode | null) => {
    if (!node) return
    if (node.kind === 'predicate' && node.predicate.kind === 'attribute' && !keys.includes(node.predicate.key))
      keys.push(node.predicate.key)
    if (node.kind === 'and' || node.kind === 'or') node.children.forEach(visit)
    if (node.kind === 'not') visit(node.child)
  }
  visit(parsed.ast)
  return keys
}

function metadataValue(metadata: unknown, path: string): unknown {
  let current: unknown = metadata
  for (const part of path.split('.')) {
    if (!current || typeof current !== 'object') return undefined
    current = (current as Record<string, unknown>)[part]
  }
  return current
}

function displayValue(value: unknown) {
  if (value === undefined || value === null) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function searchIriFilter(context: HMExploreContext, parsed: ParsedExploreQuery) {
  const scopes = compileExploreQuery(parsed, context).positiveScopes
  // Search accepts one iriFilter; widening is required for OR scopes such as in:alice OR in:bob.
  if (scopes.length > 1) return undefined
  const scope = scopes[0]
  if (scope?.kind === 'scope') {
    if (scope.scope === 'url') return `${scope.value}${scope.value.endsWith('*') ? '' : '*'}`
    if (scope.scope === 'space') return `hm://${scope.value}*`
    if (scope.scope === 'path') {
      const pathScope = scope as Extract<typeof scope, {scope: 'path'}>
      return context.type === 'site'
        ? `hm://${context.id.uid}/${pathScope.value.replace(/^\/+/, '')}${pathScope.prefix ? '*' : ''}`
        : undefined
    }
  }
  return context.type === 'site'
    ? `hm://${context.id.uid}${context.id.path?.length ? `/${context.id.path.join('/')}*` : '*'}`
    : undefined
}

function matchedFields(document: HMDocumentInfo, parsed: ParsedExploreQuery): HMExploreMatchedField[] {
  return referencedAttributes(parsed).flatMap((key) => {
    const value = displayValue(metadataValue(document.metadata, key))
    return value ? [{kind: 'attribute' as const, label: key, value, attributePath: key.split('.')}] : []
  })
}

/** Assembles fake or fetched stream pages into stable Explore results. */
export function assembleExploreResults(input: {
  parsed: ParsedExploreQuery
  context: HMExploreContext
  documentPages?: ExploreDocumentPage[]
  textPages?: ExploreTextPage[]
  intersectionTruncated?: boolean
  intersectionPending?: boolean
}): ExploreAssembly {
  const selection = exploreStreamSelection(input.parsed, input.context)
  const documentPages = selection.documents ? input.documentPages : undefined
  const textPages = selection.text ? input.textPages : undefined
  const compilation = compileExploreQuery(input.parsed, input.context)
  const documentResults = new Map<string, HMExploreResult>()
  const documentIds = new Set<string>()
  for (const page of documentPages || []) {
    for (const rawDocument of page.documents) {
      const document = rawDocument
      const result = documentInfoToExploreResultDocument(document, matchedFields(document, input.parsed))
      documentResults.set(resultKey(result), result)
      documentIds.add(exploreDocumentKey(document.id))
    }
  }

  const textResults = new Map<string, HMExploreResult>()
  for (const page of textPages || []) {
    for (const entity of page.entities) {
      const result = searchResultItemToExploreResult(entity)
      if (!result) continue
      if (compilation.requestedTypes.length && !compilation.requestedTypes.includes(result.type)) continue
      if (compilation.excludedTypes.includes(result.type)) continue
      // Text hits only survive an intersection once the document side has loaded and contains their parent.
      if (selection.intersection && (input.intersectionPending || !documentIds.has(parentKey(result)))) continue
      textResults.set(resultKey(result), result)
    }
  }

  const combined = new Map<string, HMExploreResult>()
  const intersection = selection.intersection
  if (!intersection) {
    for (const result of Array.from(documentResults.values())) combined.set(resultKey(result), result)
  }
  for (const result of Array.from(textResults.values())) {
    const key = resultKey(result)
    const document = documentResults.get(key)
    if (document && (result.type === 'document' || result.type === 'space')) {
      combined.set(key, {
        ...document,
        matchText: result.matchText,
        matchedFields: document.matchedFields,
      })
    } else {
      combined.set(key, result)
    }
  }

  const results = Array.from(combined.values()).filter(
    (result) =>
      (compilation.requestedTypes.length === 0 || compilation.requestedTypes.includes(result.type)) &&
      !compilation.excludedTypes.includes(result.type),
  )
  const documents = results.filter((result) => result.type === 'document')
  const blocks = results.filter((result) => result.type === 'block')
  const comments = results.filter((result) => result.type === 'comment')
  const spaces = results.filter((result) => result.type === 'space')
  const contacts = results.filter((result) => result.type === 'contact')
  const blocksByDocument: ExploreAssembly['blocksByDocument'] = {}
  for (const block of blocks) {
    if (block.type !== 'block') continue
    const key = parentKey(block)
    ;(blocksByDocument[key] ||= []).push(block)
  }
  return {
    results,
    documents,
    blocks,
    comments,
    spaces,
    contacts,
    counts: {
      all: results.length,
      document: documents.length,
      block: blocks.length,
      comment: comments.length,
      space: spaces.length,
      contact: contacts.length,
    },
    textTerms: compilation.textTerms.map((term) => (term.phrase ? `"${term.value}"` : term.value)),
    diagnostics: input.parsed.diagnostics.concat(compilation.diagnostics),
    intersectionTruncated: input.intersectionTruncated ?? false,
    intersectionPending: input.intersectionPending ?? (selection.intersection && !documentPages),
    blocksByDocument,
  }
}

function textPageFromResponse(response: {entities: SearchResultItem[]; nextPageToken?: string}): ExploreTextPage {
  return {entities: response.entities, nextPageToken: response.nextPageToken || ''}
}

function contentTypeFilters(types: HMExploreResultType[]) {
  if (types.length === 1 && types[0] === 'comment') return [ContentTypeFilter.CONTENT_TYPE_COMMENT]
  if (types.length === 1 && types[0] === 'block') return [ContentTypeFilter.CONTENT_TYPE_DOCUMENT]
  if (types.length === 1 && types[0] === 'contact') return [ContentTypeFilter.CONTENT_TYPE_CONTACT]
  // Spaces match on the title of their root document, so narrowing to spaces narrows to titles.
  if (types.length === 1 && types[0] === 'space') return [ContentTypeFilter.CONTENT_TYPE_TITLE]
  return [
    ContentTypeFilter.CONTENT_TYPE_TITLE,
    ContentTypeFilter.CONTENT_TYPE_DOCUMENT,
    ContentTypeFilter.CONTENT_TYPE_COMMENT,
    ContentTypeFilter.CONTENT_TYPE_CONTACT,
  ]
}

function entityKindFilters(types: HMExploreResultType[]) {
  if (types.length === 1 && types[0] === 'comment') return [EntityKindFilter.ENTITY_KIND_COMMENT]
  if (types.length === 1 && types[0] === 'block') return [EntityKindFilter.ENTITY_KIND_DOCUMENT]
  if (types.length === 1 && types[0] === 'contact') return [EntityKindFilter.ENTITY_KIND_CONTACT]
  if (types.length === 1 && types[0] === 'space') return [EntityKindFilter.ENTITY_KIND_SPACE]
  return [
    EntityKindFilter.ENTITY_KIND_SPACE,
    EntityKindFilter.ENTITY_KIND_DOCUMENT,
    EntityKindFilter.ENTITY_KIND_COMMENT,
    EntityKindFilter.ENTITY_KIND_CONTACT,
  ]
}

/** Shared TanStack Query hook for desktop and web Explore views. */
export function useExploreResults(
  inputParsed: ParsedExploreQuery,
  context: HMExploreContext,
  options: {enabled?: boolean; pageSize?: number; typeFilter?: HMExploreResultType | null} = {},
) {
  const client = useUniversalClient()
  const perspectiveAccountUid = useSelectedAccountId()
  // The reader's open result tab narrows the request to a type.
  const parsed = useMemo(
    () => withExploreTypeFilter(inputParsed, options.typeFilter),
    [inputParsed, options.typeFilter],
  )
  const compilation = compileExploreQuery(parsed, context)
  const enabled = options.enabled ?? true
  const pageSize = options.pageSize ?? 50
  const hasText = compilation.textTerms.length > 0
  const hasDocuments = compilation.documentPredicates.length > 0
  const shouldFetchDocuments = enabled && hasDocuments && Boolean(client.queryDocuments)
  const shouldFetchText = enabled && hasText

  const documentQuery = useInfiniteQuery({
    queryKey: [queryKeys.ENTITY, 'explore-documents', parsed.ast, context, parsed.presentation],
    enabled: shouldFetchDocuments,
    queryFn: async ({pageParam = '', signal}: {pageParam?: string; signal?: AbortSignal}) => {
      if (!client.queryDocuments) throw new Error('QueryDocuments is unavailable on this platform.')
      const documents: HMDocumentInfo[] = []
      let nextPageToken = pageParam
      do {
        const response = await client.queryDocuments(
          new QueryDocumentsRequest({
            filter: compilation.filter,
            pageSize,
            pageToken: nextPageToken,
            sort: (parsed.presentation.sort || []).map(
              (rule) => new DocumentSort({key: rule.key, descending: rule.direction === 'desc'}),
            ),
          }),
          {signal},
        )
        documents.push(...response.documents.map((document) => prepareHMDocumentInfo(document)))
        nextPageToken = response.nextPageToken
        if (!hasText || documents.length >= EXPLORE_INTERSECTION_DOCUMENT_CAP || !nextPageToken) break
      } while (nextPageToken)
      const truncatedByCap = hasText && documents.length >= EXPLORE_INTERSECTION_DOCUMENT_CAP && Boolean(nextPageToken)
      return {
        documents: documents.slice(0, hasText ? EXPLORE_INTERSECTION_DOCUMENT_CAP : undefined),
        nextPageToken: truncatedByCap ? '' : nextPageToken,
        truncatedByCap,
      }
    },
    getNextPageParam: (page) => page.nextPageToken || undefined,
  })

  const textQuery = useInfiniteQuery({
    queryKey: [
      queryKeys.ENTITY,
      'explore-text',
      parsed.ast,
      context,
      compilation.requestedTypes,
      perspectiveAccountUid || null,
    ],
    enabled: shouldFetchText,
    queryFn: async ({pageParam = '', signal}: {pageParam?: string; signal?: AbortSignal}) => {
      const response = await client.request(
        'Search',
        {
          query: compilation.textTerms.map((term) => (term.phrase ? `"${term.value}"` : term.value)).join(' '),
          includeBody: true,
          contextSize: 96,
          perspectiveAccountUid: perspectiveAccountUid || undefined,
          pageSize,
          pageToken: pageParam,
          iriFilter: searchIriFilter(context, parsed),
          contentTypeFilter: contentTypeFilters(compilation.requestedTypes),
          entityKindFilter: entityKindFilters(compilation.requestedTypes),
        },
        {signal},
      )
      return textPageFromResponse(response)
    },
    getNextPageParam: (page) => page.nextPageToken || undefined,
  })

  const selection = exploreStreamSelection(parsed, context)
  const documentPages = selection.documents ? documentQuery.data?.pages : undefined
  const textPages = selection.text ? textQuery.data?.pages : undefined
  const assembly = assembleExploreResults({
    parsed,
    context,
    documentPages,
    textPages,
    intersectionTruncated: Boolean(documentPages?.some((page) => page.truncatedByCap)),
    intersectionPending: selection.intersection && !documentPages,
  })
  const loadMore = async () => {
    await Promise.all([
      selection.documents && documentQuery.hasNextPage ? documentQuery.fetchNextPage() : undefined,
      selection.text && textQuery.hasNextPage ? textQuery.fetchNextPage() : undefined,
    ])
  }
  return {
    ...assembly,
    documentStream: {
      isLoading: selection.documents && documentQuery.isLoading,
      error: selection.documents ? documentQuery.error : null,
      hasMore: selection.documents && Boolean(documentQuery.hasNextPage),
    },
    textStream: {
      isLoading: selection.text && textQuery.isLoading,
      error: selection.text ? textQuery.error : null,
      hasMore: selection.text && Boolean(textQuery.hasNextPage),
    },
    isLoading: (selection.documents && documentQuery.isLoading) || (selection.text && textQuery.isLoading) || false,
    isRefetching:
      (selection.documents && documentQuery.isFetching && !documentQuery.isLoading) ||
      (selection.text && textQuery.isFetching && !textQuery.isLoading) ||
      false,
    error: (selection.documents ? documentQuery.error : null) || (selection.text ? textQuery.error : null) || null,
    loadMore,
    hasMore: Boolean((selection.documents && documentQuery.hasNextPage) || (selection.text && textQuery.hasNextPage)),
  }
}

/**
 * The most recently active documents in scope, for the Explore landing state.
 *
 * Ordered by activity rather than update time. Scoped to one space, or across
 * the node when Explore is unscoped.
 */
export function useExploreRecentDocuments(
  context: HMExploreContext,
  options: {enabled?: boolean; pageSize?: number; rootsOnly?: boolean} = {},
) {
  const client = useUniversalClient()
  const pageSize = options.pageSize ?? 10
  const rootsOnly = options.rootsOnly ?? false
  return useQuery({
    queryKey: [queryKeys.ENTITY, 'explore-recent-documents', context, pageSize, rootsOnly],
    enabled: (options.enabled ?? true) && Boolean(client.queryDocuments),
    queryFn: async ({signal}: {signal?: AbortSignal} = {}): Promise<HMDocumentInfo[]> => {
      if (!client.queryDocuments) return []
      // An empty path selects root documents, which is how a space is addressed, so `rootsOnly`
      // turns this same query into a list of spaces.
      const scopeFilters = [
        context.type === 'site'
          ? new DocumentFilter({
              filter: {case: 'spaceMatch', value: new DocumentFilter_SpaceMatch({space: context.id.uid})},
            })
          : null,
        rootsOnly
          ? new DocumentFilter({
              filter: {case: 'pathMatch', value: new DocumentFilter_PathMatch({path: '', prefix: false})},
            })
          : null,
      ].filter((filter): filter is DocumentFilter => !!filter)
      const response = await client.queryDocuments(
        new QueryDocumentsRequest({
          filter:
            scopeFilters.length === 0
              ? undefined
              : scopeFilters.length === 1
                ? scopeFilters[0]
                : new DocumentFilter({filter: {case: 'and', value: new DocumentFilter_And({filters: scopeFilters})}}),
          sort: [new DocumentSort({attribute: BuiltinSortAttribute.UPDATE_TIME, descending: true})],
          pageSize,
        }),
        {signal},
      )
      return response.documents.map((document) => prepareHMDocumentInfo(document))
    },
  })
}

// The spaces this identity has joined.
export function useExploreJoinedSpaces(options: {enabled?: boolean} = {}) {
  const client = useUniversalClient()
  const selectedAccountUid = useSelectedAccountId()
  const contacts = useSelectedAccountContacts()

  // The same space joined twice leaves one contact record per join.
  // The backend returns them newest first, so the first per subject wins.
  const joinedUids = Array.from(
    new Set([
      ...(selectedAccountUid ? [selectedAccountUid] : []),
      ...(contacts.data ?? []).filter((contact) => contact.subscribe?.site).map((contact) => contact.subject),
    ]),
  )

  const query = useQuery({
    queryKey: [queryKeys.ENTITY, 'explore-joined-spaces', joinedUids],
    enabled: (options.enabled ?? true) && Boolean(client.queryDocuments) && joinedUids.length > 0,
    queryFn: async ({signal}: {signal?: AbortSignal} = {}): Promise<HMDocumentInfo[]> => {
      if (!client.queryDocuments) return []
      const response = await client.queryDocuments(
        new QueryDocumentsRequest({
          filter: new DocumentFilter({
            filter: {
              case: 'and',
              value: new DocumentFilter_And({
                filters: [
                  new DocumentFilter({
                    filter: {
                      case: 'or',
                      value: new DocumentFilter_Or({
                        filters: joinedUids.map(
                          (uid) =>
                            new DocumentFilter({
                              filter: {case: 'spaceMatch', value: new DocumentFilter_SpaceMatch({space: uid})},
                            }),
                        ),
                      }),
                    },
                  }),
                  // An empty path selects the root document of each space.
                  new DocumentFilter({
                    filter: {case: 'pathMatch', value: new DocumentFilter_PathMatch({path: '', prefix: false})},
                  }),
                ],
              }),
            },
          }),
          sort: [new DocumentSort({attribute: BuiltinSortAttribute.ACTIVITY_TIME, descending: true})],
          pageSize: joinedUids.length,
        }),
        {signal},
      )
      return response.documents.map((document) => prepareHMDocumentInfo(document))
    },
  })

  return {...query, isLoading: contacts.isLoading || query.isLoading}
}

// One account shown by the Explore People list.
export type ExploreAccount = {uid: string; name?: string; icon?: string}

// Accounts known to this node, for the Explore People list.
export function useExploreAccountList(options: {enabled?: boolean; pageSize?: number} = {}) {
  const client = useUniversalClient()
  const pageSize = options.pageSize ?? 50
  return useQuery({
    queryKey: [queryKeys.ENTITY, 'explore-account-list', pageSize],
    enabled: (options.enabled ?? true) && Boolean(client.listAccounts),
    queryFn: async (): Promise<ExploreAccount[]> => {
      if (!client.listAccounts) return []
      const response = await client.listAccounts(new ListAccountsRequest({pageSize}))
      return response.accounts
        .filter((account) => !account.aliasAccount)
        .map((account) => ({
          uid: account.id,
          name: account.profile?.name || undefined,
          icon: account.profile?.icon || undefined,
        }))
    },
  })
}

/**
 * The people of one space, as the space's own People tab lists them.
 *
 * ListDocumentCollaborators returns the publisher, the members holding
 * a granted capability, and the plain members. Ordered publisher
 * first, then granted members, then the rest, matching the tab.
 */
export function useExploreSpacePeople(spaceId: UnpackedHypermediaId | null, options: {enabled?: boolean} = {}) {
  const enabled = options.enabled ?? true
  const {accounts, grantedMembers, members, isInitialLoading} = useSiteMembers(enabled && spaceId ? spaceId : null)
  const data = useMemo(() => {
    if (!enabled || !spaceId) return []
    const toAccount = (uid: string, role: string): ExploreAccount & {role: string} => ({
      uid,
      name: accounts[uid]?.metadata?.name || undefined,
      icon: accounts[uid]?.metadata?.icon || undefined,
      role,
    })
    const seen = new Set<string>()
    // The publisher owns the space and heads the tab's list.
    const people = [toAccount(spaceId.uid, 'owner')]
    seen.add(spaceId.uid)
    for (const member of [...grantedMembers, ...members]) {
      if (seen.has(member.account.uid)) continue
      seen.add(member.account.uid)
      people.push(toAccount(member.account.uid, member.role))
    }
    return people
  }, [enabled, spaceId, accounts, grantedMembers, members])
  // A disabled query reports isLoading forever, which would leave
  // the caller on a spinner whenever there is no space to ask about.
  return {data, isLoading: isInitialLoading}
}

/**
 * Document records for the search results on screen, fetched in one request.
 *
 * Full-text search returns matched content and an id, so a search result
 * has no name, authors, attributes or activity. Run a single QueryDocuments
 * call as an OR of URL matches.
 */
export function useExploreResultDocuments(results: HMExploreResult[], options: {enabled?: boolean} = {}) {
  const client = useUniversalClient()
  // Only documents and spaces have records to fetch.
  const urls = Array.from(
    new Set(
      results
        .filter((result) => result.type === 'document' || result.type === 'space')
        .map((result) =>
          packHmId({
            ...(result as Extract<HMExploreResult, {type: 'document' | 'space'}>).id,
            version: null,
            blockRef: null,
            blockRange: null,
            latest: null,
          }),
        ),
    ),
  ).sort()

  const query = useQuery({
    queryKey: [queryKeys.ENTITY, 'explore-result-documents', urls],
    enabled: (options.enabled ?? true) && Boolean(client.queryDocuments) && urls.length > 0,
    queryFn: async ({signal}: {signal?: AbortSignal} = {}): Promise<Record<string, HMDocumentInfo>> => {
      if (!client.queryDocuments || !urls.length) return {}
      const response = await client.queryDocuments(
        new QueryDocumentsRequest({
          filter: new DocumentFilter({
            filter: {
              case: 'or',
              value: new DocumentFilter_Or({
                filters: urls.map(
                  (url) =>
                    new DocumentFilter({
                      filter: {case: 'urlMatch', value: new DocumentFilter_URLMatch({url, prefix: false})},
                    }),
                ),
              }),
            },
          }),
          pageSize: urls.length,
        }),
        {signal},
      )
      const byKey: Record<string, HMDocumentInfo> = {}
      for (const raw of response.documents) {
        const document = prepareHMDocumentInfo(raw)
        byKey[exploreDocumentKey(document.id)] = document
      }
      return byKey
    },
  })

  return {...query, data: query.data ?? {}}
}
